import {
  Box,
  Flex,
  Grid,
  Input,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  TextLabel,
  Textarea,
} from '@tonic-one/react';
import './index.less';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { GlobalApi } from '@Apis';
import CSpinner from '@Components/CSpinner';
import ErrorAlert from '@Components/Common/ErrorAlert';
import LoadingBtn from '@Components/Common/LoadingBtn';
import MenuWithLabel from 'src/components/Common/Menu';
import { MenuOption } from '@Types/index';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import { produce } from 'immer';
import i18next from 'i18next';
import { SsoConfigDto } from '@Types/administrator';

enum IDPType {
  AzureadAdfs = '1',
  Okta = '2',
  Google = '3',
  Pingone = '4',
}

const IdpMenuOptions: MenuOption[] = [
  {
    key: IDPType.AzureadAdfs,
    value: 'administration.singleSignOnSettings.idpList.azureadAdfs',
  },
  {
    key: IDPType.Okta,
    value: 'administration.singleSignOnSettings.idpList.okta',
  },
  {
    key: IDPType.Google,
    value: 'administration.singleSignOnSettings.idpList.google',
  },
  {
    key: IDPType.Pingone,
    value: 'administration.singleSignOnSettings.idpList.pingone',
  },
];

interface SsoConfig {
  enableSamlSso: boolean;
  idpLoginUrl: string;
  identifiedName: string;
  certificate: string;
  idpType: string;
}

const SingleSignOn = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [ssoConfig, setSsoConfig] = useState<SsoConfig>({
    enableSamlSso: false,
    idpLoginUrl: '',
    identifiedName: '',
    certificate: '',
    idpType: IDPType.AzureadAdfs,
  });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const ssoConfigDto: SsoConfigDto | {} = (await GlobalApi.getSsoConfig())
          .data;

        if (Object.keys(ssoConfigDto).length === 0) {
          return;
        }

        // Type guard to ensure ssoConfigDto is SsoConfigDto
        const config = ssoConfigDto as SsoConfigDto;
        setSsoConfig({
          enableSamlSso: !!config.enableSamlSso,
          idpType: config.idpType?.toString() || IDPType.AzureadAdfs,
          idpLoginUrl: config.idpLoginUrl || '',
          identifiedName: config.identifiedName || '',
          certificate: config.certificate || '',
        });
      } catch (error: any) {
        if (error?.status === 404) {
          return;
        }
        console.error(error);
        setErrorMsg(t('common.apiException.internalError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const onSave = async () => {
    try {
      setLoading(true);
      const {
        enableSamlSso,
        idpType,
        idpLoginUrl,
        identifiedName,
        certificate,
      } = ssoConfig;
      const ssoConfigDto: SsoConfigDto = {
        enableSamlSso: enableSamlSso ? 1 : 0,
        idpLoginUrl,
        identifiedName,
        certificate,
        idpType: Number(idpType),
      };
      await GlobalApi.updateSsoConfig(ssoConfigDto);
    } catch (error) {
      console.log(JSON.stringify(error));
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  };

  const AdfsPopoverLink = ({ children }: any) => {
    const { t } = useTranslation();
    return (
      <>
        <Popover arrow={true} trigger="hover" enterDelay={500}>
          <PopoverTrigger>
            <Link
              href={getCasOLHLink(
                CAS_DOC_LINK.administrator.singleSignOn.adfs,
                i18next.language
              )}
              target="_blank"
            >
              {children[0]}
            </Link>
          </PopoverTrigger>
          <PopoverContent
            maxWidth="600px"
            PopperProps={{
              usePortal: true,
            }}
          >
            {t('administration.singleSignOnSettings.resourceTips')}
          </PopoverContent>
        </Popover>
      </>
    );
  };
  return (
    <Flex direction="column">
      {loading && <CSpinner />}
      {errorMsg && (
        <ErrorAlert
          errorMsg={errorMsg}
          onClose={() => setErrorMsg(undefined)}
        />
      )}
      <Box flexShrink="0" pl="16px">
        <Text fontWeight="400" fontSize="18px" lineHeight="24px" py="16px">
          {t('administration.singleSignOnSettings.sso')}
        </Text>
        <Box>
          <Text>
            <Trans i18nKey="administration.singleSignOnSettings.description">
              placeholder
              <Link
                href={getCasOLHLink(
                  CAS_DOC_LINK.administrator.singleSignOn.azureAd,
                  i18next.language
                )}
                target="_blank"
              />
              <AdfsPopoverLink />
              <Link
                href={getCasOLHLink(
                  CAS_DOC_LINK.administrator.singleSignOn.okta,
                  i18next.language
                )}
                target="_blank"
              />
              <Link
                href={getCasOLHLink(
                  CAS_DOC_LINK.administrator.singleSignOn.google,
                  i18next.language
                )}
                target="_blank"
              />
              <Link
                href={getCasOLHLink(
                  CAS_DOC_LINK.administrator.singleSignOn.pingOne,
                  i18next.language
                )}
                target="_blank"
              />
            </Trans>
          </Text>
        </Box>
      </Box>
      <Grid
        templateColumns="180px max-content"
        columnGap="10x"
        rowGap="18px"
        marginLeft={24}
        marginTop={20}
      >
        <TextLabel className="itemLabel">
          {t('administration.singleSignOnSettings.enableSsoSetting')}:
          <span className="requiredField">*</span>
        </TextLabel>
        <Switch
          width={60}
          checked={ssoConfig.enableSamlSso}
          onChange={() =>
            setSsoConfig(
              produce(ssoConfig, (draft) => {
                draft.enableSamlSso = !ssoConfig.enableSamlSso;
              })
            )
          }
        />
        <TextLabel className="itemLabel">
          {t('administration.singleSignOnSettings.IDPType')}:
          <span className="requiredField">*</span>
        </TextLabel>
        <MenuWithLabel
          menuItems={IdpMenuOptions.map((item) => {
            return { key: item.key, value: t(item.value) };
          })}
          minWidth={800}
          selectedKey={ssoConfig.idpType}
          onClick={(event: any) => {
            const val = event.target.getAttribute('value');
            setSsoConfig(
              produce(ssoConfig, (draft) => {
                draft.idpType = val;
              })
            );
          }}
          disabled={!ssoConfig.enableSamlSso}
        />
        <TextLabel className="itemLabel">
          {t('administration.singleSignOnSettings.IDPLoginUrl')}:
          <span className="requiredField">*</span>
        </TextLabel>
        <Box>
          <Input
            type="text"
            width={800}
            disabled={!ssoConfig.enableSamlSso}
            value={ssoConfig.idpLoginUrl}
            onChange={(e) =>
              setSsoConfig(
                produce(ssoConfig, (draft) => {
                  draft.idpLoginUrl = e.target.value;
                })
              )
            }
            required={ssoConfig.enableSamlSso}
          />
          <Box
            dangerouslySetInnerHTML={{
              __html: t(
                'administration.singleSignOnSettings.IDPLoginUrlPlaceHolder'
              ),
            }}
          />
        </Box>
        <TextLabel className="itemLabel">
          {t('administration.singleSignOnSettings.appIdentity')}:
          <span className="requiredField">*</span>
        </TextLabel>
        <Input
          type="text"
          width={800}
          disabled={!ssoConfig.enableSamlSso}
          value={ssoConfig.identifiedName}
          onChange={(e) =>
            setSsoConfig(
              produce(ssoConfig, (draft) => {
                draft.identifiedName = e.target.value;
              })
            )
          }
          required={ssoConfig.enableSamlSso}
        />
        <TextLabel className="itemLabel">
          {t('administration.singleSignOnSettings.certificate')}:
          <span className="requiredField">*</span>
        </TextLabel>
        <Textarea
          mt="1x"
          width={800}
          height={120}
          disabled={!ssoConfig.enableSamlSso}
          value={ssoConfig.certificate}
          onChange={(e) =>
            setSsoConfig(
              produce(ssoConfig, (draft) => {
                draft.certificate = e.target.value;
              })
            )
          }
          required={ssoConfig.enableSamlSso}
          placeholder={t(
            'administration.singleSignOnSettings.certificatePlaceholder'
          )}
        />
      </Grid>
      <LoadingBtn
        width="20x"
        marginTop={40}
        marginLeft={960}
        onClick={onSave}
        disabled={
          ssoConfig.enableSamlSso === true &&
          (ssoConfig.idpLoginUrl.length === 0 ||
            ssoConfig.identifiedName.length === 0 ||
            ssoConfig.certificate.length === 0)
        }
      >
        {(loading: boolean) =>
          loading
            ? t('common.action.label.saving')
            : t('common.action.label.save')
        }
      </LoadingBtn>
    </Flex>
  );
};
export default SingleSignOn;
